import { c as _c } from "react/compiler-runtime";
import classNames from 'classnames';
import type { ElementType, HTMLAttributes, ReactNode } from 'react';
import styles from './Section.module.scss';
export type SectionAs = 'section' | 'div' | 'article' | 'main';

/**
 * The band's block padding. `default`: the surface decides, on the section
 * rhythm (P: 96px from 768px, 64px below): none on the `transparent` and
 * `page` surfaces, P above and below the content on an opaque one (`subtle`,
 * `dark`, `brand`). `none`: an opaque band with no padding, for media that
 * meets the band's edges. The space between sections is the rhythm's, never
 * a prop: every Section takes P above it and nothing below it.
 */
export type SectionPadding = 'default' | 'none';

/**
 * The band's surface: `page` (the page colour: white, `#080808` in the dark
 * theme; formerly `warm`), `subtle` (`#f4f4f4`, the redesign's grey band),
 * `dark` or `brand`. `transparent` (the default) sets no surface.
 */
export type SectionSurface = 'transparent' | 'page' | 'subtle' | 'dark' | 'brand';
export interface SectionProps extends Omit<HTMLAttributes<HTMLElement>, 'className' | 'style'> {
  as?: SectionAs;
  children: ReactNode;
  className?: never;
  padding?: SectionPadding;
  style?: never;
  surface?: SectionSurface;
}

// The opaque surfaces: a band pads P inside and abuts a band before it.
const BAND_SURFACES: ReadonlySet<SectionSurface> = new Set(['subtle', 'dark', 'brand']);
function Section(t0) {
  const $ = _c(17);
  let children;
  let props;
  let t1;
  let t2;
  let t3;
  if ($[0] !== t0) {
    const {
      as: t4,
      children: t5,
      className: _className,
      padding: t6,
      style: _style,
      surface: t7,
      ...t8
    } = t0;
    t1 = t4;
    children = t5;
    t2 = t6;
    t3 = t7;
    props = t8;
    $[0] = t0;
    $[1] = children;
    $[2] = props;
    $[3] = t1;
    $[4] = t2;
    $[5] = t3;
  } else {
    children = $[1];
    props = $[2];
    t1 = $[3];
    t2 = $[4];
    t3 = $[5];
  }
  const as = t1 === undefined ? "section" : t1;
  const padding = t2 === undefined ? "default" : t2;
  const surface = t3 === undefined ? "transparent" : t3;
  const Component = as as ElementType;
  const surfaceTone = surface === "transparent" ? undefined : surface;
  const band = BAND_SURFACES.has(surface) ? padding === "none" ? "flush" : "padded" : undefined;
  const t4 = band === "padded" && styles[`helios-section--band-${surface}`];
  const t5 = band === "flush" && styles["helios-section--band-flush"];
  const t6 = styles[`helios-section--surface-${surface}`];
  let t7;
  if ($[6] !== t4 || $[7] !== t5 || $[8] !== t6) {
    t7 = classNames(styles["helios-section"], t4, t5, t6);
    $[6] = t4;
    $[7] = t5;
    $[8] = t6;
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  let t8;
  if ($[10] !== Component || $[11] !== band || $[12] !== children || $[13] !== props || $[14] !== surfaceTone || $[15] !== t7) {
    t8 = <Component {...props} data-helios-band={band} data-helios-section="" data-helios-surface={surfaceTone} className={t7}>{children}</Component>;
    $[10] = Component;
    $[11] = band;
    $[12] = children;
    $[13] = props;
    $[14] = surfaceTone;
    $[15] = t7;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  return t8;
}
export default Section;